class PredictiveSearch extends HTMLElement {
  constructor() {
    super();

    this.input = this.querySelector('input[type="search"]');
    this.resultsContainer = this.querySelector('[data-predictive-search]');
    this.abortController = null;
    this.searchTimeout = null;
    this.isOpen = false;

    if (!this.input || !this.resultsContainer) return;

    this.input.addEventListener('input', this.onInput.bind(this));
  }

  onInput() {
    clearTimeout(this.searchTimeout);

    this.searchTimeout = setTimeout(() => {
      this.getSearchResults();
    }, 150);
  }

  async getSearchResults() {
    const query = this.input.value.trim();

    if (query.length < 1) {
      this.closeResults();
      return;
    }

    if (this.abortController) {
      this.abortController.abort();
    }

    this.abortController = new AbortController();
    this.setAttribute('loading', '');

    try {
      const isArabic = document.documentElement.lang.toLowerCase().includes('ar');

      if (isArabic) {
        await this.getArabicSearchResults(query);
      } else {
        await this.getPredictiveSearchResults(query);
      }
    } catch (error) {
      if (error.name !== 'AbortError') {
        this.closeResults();
      }
    } finally {
      this.removeAttribute('loading');
    }
  }

  async getPredictiveSearchResults(query) {
    const searchUrl = window.routes?.predictive_search_url || '/search/suggest';
    const url = `${searchUrl}?q=${encodeURIComponent(query)}&resources[type]=product&resources[limit]=10&section_id=predictive-search`;

    const response = await fetch(url, {
      signal: this.abortController.signal,
    });

    const text = await response.text();
    const html = new DOMParser().parseFromString(text, 'text/html');
    const results = html.querySelector('#predictive-search-results');

    if (results) {
      this.resultsContainer.innerHTML = results.innerHTML;
      this.openResults();
    } else {
      this.closeResults();
    }
  }

  async getArabicSearchResults(query) {
    const url = `/search?q=${encodeURIComponent(query)}&type=product`;

    const response = await fetch(url, {
      signal: this.abortController.signal,
    });

    const text = await response.text();
    const html = new DOMParser().parseFromString(text, 'text/html');

    const cards = Array.from(
      html.querySelectorAll('#product-grid .grid__item, .product-grid .grid__item')
    ).slice(0, 10);

    if (!cards.length) {
      this.resultsContainer.innerHTML = `
        <div id="predictive-search-results" role="listbox">
          <p class="predictive-search__heading">لا توجد نتائج</p>
        </div>
      `;
      this.openResults();
      return;
    }

    const items = cards.map((card, index) => {
      const link = card.querySelector('.card__heading a, a.full-unstyled-link[href*="/products/"]');
      const image = card.querySelector('img');
      const price = card.querySelector('.price-item--sale, .price-item--regular');

      if (!link) return '';

      const title = this.escapeHtml(link.textContent.trim());
      const href = link.getAttribute('href') || '#';
      const imageSrc = image ? image.getAttribute('src') || image.getAttribute('data-src') : '';
      const priceText = price ? this.escapeHtml(price.textContent.trim()) : '';

      return `
        <li class="predictive-search__list-item" role="option" aria-selected="false">
          <a
            href="${href}"
            class="predictive-search__item predictive-search__item--link-with-thumbnail link link--text"
            tabindex="-1"
          >
            ${
              imageSrc
                ? `<img class="predictive-search__image" src="${imageSrc}" alt="${title}" width="50" height="50">`
                : ''
            }
            <div class="predictive-search__item-content predictive-search__item-content--centered">
              <p class="predictive-search__item-heading h5">${title}</p>
              ${priceText ? `<div class="price">${priceText}</div>` : ''}
            </div>
          </a>
        </li>
      `;
    }).join('');

    this.resultsContainer.innerHTML = `
      <div id="predictive-search-results" role="listbox">
        <div class="predictive-search__results-groups-wrapper predictive-search__results-groups-wrapper--no-suggestions">
          <div class="predictive-search__result-group">
            <div>
              <h2 class="predictive-search__heading text-body caption-with-letter-spacing">المنتجات</h2>
              <ul class="predictive-search__results-list list-unstyled" role="group">
                ${items}
              </ul>
            </div>
          </div>
        </div>
      </div>
    `;

    this.openResults();
  }

  openResults() {
    this.isOpen = true;
    this.setAttribute('open', '');
    this.input.setAttribute('aria-expanded', 'true');
    this.resultsContainer.style.display = 'block';
  }

  closeResults() {
    this.isOpen = false;
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', 'false');
    this.resultsContainer.innerHTML = '';
    this.resultsContainer.style.display = '';
  }

  escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }
}

if (!customElements.get('predictive-search')) {
  customElements.define('predictive-search', PredictiveSearch);
}
